import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import Typography from '../../../core/Typography';
import Carousel from '../../../core/Carousel';
import { SvgIcon } from '../../../core/SvgIcon/SvgIcon';
import clamp from '../../../../utils/clamp';

import useStyles from './styles';
import ICardImage from './types';
import {
  CardImageMediaTypes,
  CardImagePosition,
  CardLayout,
  ICardImageMedia,
  IImage,
  IVideo,
} from './types';

export interface IRenderCardImageHeader {
  media: ICardImageMedia;
  currentCarouselIndex?: number;
  onCarouselImageChange: (id: number) => void;
}

function RenderCardImageHeader({
  media,
  currentCarouselIndex,
  onCarouselImageChange,
}: IRenderCardImageHeader) {
  const styles = useStyles();
  const { type, images = [], videos = [] } = media;

  switch (type) {
    case CardImageMediaTypes.Image:
      const [image] = images;
      return (
        <LazyLoadImage src={image.src} alt={image.alt} width={image.width} height={image.height} />
      );
    case CardImageMediaTypes.Video:
      return (
        <video controls>
          {videos.map((video, index) => (
            <source
              key={index}
              src={video.src}
              type={video.type}
              width={video.width}
              height={video.height}
            />
          ))}
        </video>
      );
    case CardImageMediaTypes.Carousel:
      return (
        <Carousel
          classNameImage={styles.carouselImage}
          items={images}
          current={currentCarouselIndex}
          showIndicator={false}
          onChange={onCarouselImageChange}
        />
      );
    default:
      return <div>No data provided</div>;
  }
}

function CardImage({
  className,
  label,
  title,
  description,
  media,
  layout = CardLayout.Vertical,
  imagePosition = CardImagePosition.Left,
}: ICardImage) {
  const styles = useStyles({ isHorizontal: layout === CardLayout.Horizontal });
  const { carouselData, type } = media;
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState<number>(0);
  const isCarousel = useMemo(() => type === CardImageMediaTypes.Carousel, [type]);

  const {
    label: convertedLabel,
    title: convertedTitle,
    description: convertedDescription,
    media: convertedMedia,
  } = useMemo(() => {
    if (isCarousel) {
      return {
        ...carouselData[currentCarouselIndex],
        media: {
          type: CardImageMediaTypes.Carousel,
          videos: carouselData.map((item) => ({ ...item.video })) as IVideo[],
          images: carouselData.map((item) => ({ ...item.image })) as IImage[],
        } as ICardImageMedia,
      };
    }

    return {
      label,
      title,
      description,
      media,
    };
  }, [isCarousel, label, title, description, media, carouselData, currentCarouselIndex]);

  const rootClasses = classNames(styles.root, className, {
    [styles.horizontal]: layout === CardLayout.Horizontal,
    [styles.withImgOnRight]:
      layout === CardLayout.Horizontal && imagePosition === CardImagePosition.Right,
  });

  const changeCarouselImage = useCallback(
    (index) => {
      const nextIndex = clamp(index, 0, convertedMedia?.images.length - 1);
      setCurrentCarouselIndex(nextIndex);
    },
    [convertedMedia?.images?.length],
  );

  return (
    <article className={rootClasses}>
      <header className={styles.image}>
        <RenderCardImageHeader
          media={convertedMedia}
          currentCarouselIndex={currentCarouselIndex}
          onCarouselImageChange={setCurrentCarouselIndex}
        />
      </header>

      <div className={styles.content}>
        <div className={styles.contentInfo}>
          {convertedLabel && (
            <Typography className={styles.label} element="h4" look="body-large">
              {convertedLabel}
            </Typography>
          )}

          <Typography className={styles.title} element="h3" look="p" color="var(--x-accent-2-1)">
            {convertedTitle}
          </Typography>

          <Typography className={styles.description} element="p" color="var(--x-text-color-2)">
            {convertedDescription}
          </Typography>
        </div>

        {isCarousel && (
          <div className={styles.controlContainer}>
            <button
              className={styles.control}
              disabled={currentCarouselIndex === 0}
              onClick={() => changeCarouselImage(currentCarouselIndex - 1)}
            >
              <SvgIcon name="arrow-carrousel-left-gen" />
            </button>

            <button
              className={styles.control}
              disabled={currentCarouselIndex === convertedMedia?.images.length - 1}
              onClick={() => changeCarouselImage(currentCarouselIndex + 1)}
            >
              <SvgIcon name="arrow-carrousel-right-gen" />
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

export default CardImage;
